import { getMarkRange, Mark, mergeAttributes } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import type { TooltipAttributes } from "./tooltip-html"
import {
	normalizeTooltipAttributes,
	tooltipElementAttributes,
	tooltipPopupSpec,
} from "./tooltip-html"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		tooltip: {
			/** Wraps the selection in a tooltip. */
			setTooltip: (attributes: Partial<TooltipAttributes>) => ReturnType
			/** Changes the tooltip at the caret or in the selection. */
			updateTooltip: (attributes: Partial<TooltipAttributes>) => ReturnType
			unsetTooltip: () => ReturnType
			/** Inserts the trigger text with its tooltip at the caret. */
			insertTooltip: (
				text: string,
				attributes: Partial<TooltipAttributes>,
			) => ReturnType
		}
	}
}

const TRIGGER_CLASS = "pl-tooltip-trigger"

// The popup sits inside the mark's element but is not content, so parsing
// reads only the trigger. Older or hand-written HTML has no trigger wrapper.
function triggerOf(element: HTMLElement): HTMLElement {
	for (const child of Array.from(element.childNodes ?? [])) {
		const candidate = child as HTMLElement
		const classes = candidate.getAttribute?.("class") ?? ""
		if (classes.split(/\s+/).includes(TRIGGER_CLASS)) return candidate
	}
	return element
}

const attribute = (name: keyof TooltipAttributes) => ({
	default: name === "tip" ? "" : null,
	parseHTML: (element: HTMLElement) => element.getAttribute(`data-${name}`),
	rendered: false,
})

export const TooltipMark = Mark.create({
	name: "tooltip",
	// Above Link's 1000: the popup's own link must never end up inside an <a>.
	priority: 1001,
	inclusive: false,

	addAttributes() {
		return {
			tip: attribute("tip"),
			headline: attribute("headline"),
			cta: attribute("cta"),
			href: attribute("href"),
		}
	},

	parseHTML() {
		return [
			{
				tag: 'span[data-type="tooltip"]',
				contentElement: (node) => triggerOf(node as HTMLElement),
			},
		]
	},

	renderHTML({ mark, HTMLAttributes }) {
		const attributes = normalizeTooltipAttributes(mark.attrs)
		return [
			"span",
			mergeAttributes(HTMLAttributes, tooltipElementAttributes(attributes)),
			["span", { class: TRIGGER_CLASS }, 0],
			tooltipPopupSpec(attributes),
		]
	},

	addCommands() {
		return {
			setTooltip:
				(attributes) =>
				({ commands }) =>
					commands.setMark(this.name, normalizeTooltipAttributes(attributes)),

			updateTooltip:
				(changes) =>
				({ state, tr, dispatch }) => {
					const type = state.schema.marks[this.name]
					if (!type) return false
					const { from, to, empty, $from } = state.selection
					const range = empty ? getMarkRange($from, type) : { from, to }
					if (!range) return false
					let changed = false
					state.doc.nodesBetween(range.from, range.to, (node, pos) => {
						if (!node.isText) return true
						const mark = type.isInSet(node.marks)
						if (!mark) return false
						const start = Math.max(pos, range.from)
						const end = Math.min(pos + node.nodeSize, range.to)
						if (dispatch) {
							tr.removeMark(start, end, type)
							tr.addMark(
								start,
								end,
								type.create(
									normalizeTooltipAttributes({ ...mark.attrs, ...changes }),
								),
							)
						}
						changed = true
						return false
					})
					return changed
				},

			unsetTooltip:
				() =>
				({ commands }) =>
					commands.unsetMark(this.name, { extendEmptyMarkRange: true }),

			insertTooltip:
				(text, attributes) =>
				({ state, tr, dispatch }) => {
					const type = state.schema.marks[this.name]
					const trigger = text.trim()
					if (!type || !trigger) return false
					if (dispatch) {
						const from = state.selection.from
						const mark = type.create(normalizeTooltipAttributes(attributes))
						tr.replaceSelectionWith(state.schema.text(trigger, [mark]), false)
						tr.setSelection(TextSelection.create(tr.doc, from + trigger.length))
						tr.setStoredMarks([])
					}
					return true
				},
		}
	},
})

export const TooltipExtensions = [TooltipMark]
